
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../actions/api"; // Your shared axios instance
import { hideLoader, showLoader } from "./loaderSlice";

export const getScheduleList = createAsyncThunk(
  "getScheduleList",
  async (_, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).get(`/getall/clinic/schedule`);

      if (res.status === 200) {
        thunkAPI.dispatch(hideLoader());
        return res?.data?.data || [];
      }
      thunkAPI.dispatch(hideLoader());
      return [];
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to fetch schedules");
    }
  }
);

export const createSchedule = createAsyncThunk(
  "createSchedule",
  async (scheduleData, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).post(
        `/create/clinic/schedule`,
        scheduleData
      );

      if (res.status === 200) {
        thunkAPI.dispatch(hideLoader());
        return res?.data?.data;
      }
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to create schedule");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  }
);

export const updateSchedule = createAsyncThunk(
  "updateSchedule",
  async ({ scheduleId, scheduleData }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).put(
        `/update/clinic/schedule/${scheduleId}`,
        scheduleData
      );

      if (res.status === 200) {
        thunkAPI.dispatch(hideLoader());
        return res?.data?.data;
      }
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to update schedule");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  }
);

export const deleteSchedule = createAsyncThunk(
  "deleteSchedule",
  async ({ scheduleId }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).delete(
        `/delete/clinic/schedule/${scheduleId}`
      );

      if (res.status === 200) {
        thunkAPI.dispatch(hideLoader());
        return scheduleId; // Return ID to remove from list
      }
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to delete schedule");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  }
);

const scheduleSlice = createSlice({
  name: "schedule",
  initialState: {
    list: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getScheduleList.fulfilled, (state, action) => {
        state.list = action.payload ?? [];
        state.loading = false;
        state.error = null;
      })
      .addCase(createSchedule.fulfilled, (state, action) => {
        // Only add to list if payload exists
        if (action.payload) {
          state.list.push(action.payload);
        }
        state.loading = false;
        state.error = null;
      })
      .addCase(updateSchedule.fulfilled, (state, action) => {
        const updated = action.payload;
        if (updated) {
          const index = state.list.findIndex((s) => s.scheduleId === updated.scheduleId);
          if (index !== -1) {
            state.list[index] = updated;
          }
        }
        state.loading = false;
        state.error = null;
      })
      .addCase(deleteSchedule.fulfilled, (state, action) => {
        const id = action.payload;
        state.list = state.list.filter((s) => s.scheduleId !== id);
        state.loading = false;
        state.error = null;
      })
      .addCase(getScheduleList.pending, (state) => {
        state.loading = true;
      })
      .addCase(createSchedule.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateSchedule.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteSchedule.pending, (state) => {
        state.loading = true;
      })
      .addCase(getScheduleList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error fetching schedules.";
      })
      .addCase(createSchedule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error creating schedule.";
      })
      .addCase(updateSchedule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error updating schedule.";
      })
      .addCase(deleteSchedule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error deleting schedule.";
      });
  },
});

export default scheduleSlice.reducer;